import { useState } from "react";
import {
  SquareDashedBottom,
  Box,
  Heading,
  Type,
  ImageIcon,
  MousePointerClick,
  AppWindow,
  Grid,
  Tag,
  Minus,
  MoveVertical,
  ShoppingBag,
  Sparkles,
  ShieldCheck,
  Star,
  Mail,
  Plus,
} from "lucide-react";
import { useEditorStore } from "../store";
import type { ComponentType } from "@zirro/schema";
import { ECOMMERCE_SECTION_PRESETS } from "../ecommerce-templates";

interface PaletteItem {
  type: ComponentType;
  label: string;
  description: string;
  icon: typeof Box;
}

const PALETTE_ITEMS: PaletteItem[] = [
  {
    type: "Section",
    label: "Section",
    description: "Full-width page layout block.",
    icon: SquareDashedBottom,
  },
  {
    type: "Container",
    label: "Container",
    description: "Flexible flexbox content wrapper.",
    icon: Box,
  },
  {
    type: "Card",
    label: "Card",
    description: "Feature container with padding & shadow.",
    icon: AppWindow,
  },
  {
    type: "FeatureGrid",
    label: "Feature Grid",
    description: "Multi-column responsive grid layout.",
    icon: Grid,
  },
  {
    type: "Heading",
    label: "Heading",
    description: "H1–H6 title text block.",
    icon: Heading,
  },
  {
    type: "Text",
    label: "Paragraph",
    description: "Body text with rich formatting.",
    icon: Type,
  },
  {
    type: "Button",
    label: "Button",
    description: "Call to action link button.",
    icon: MousePointerClick,
  },
  {
    type: "Image",
    label: "Image",
    description: "Responsive image with alt text.",
    icon: ImageIcon,
  },
  {
    type: "Badge",
    label: "Badge",
    description: "Pill tag for callouts or highlights.",
    icon: Tag,
  },
  {
    type: "Divider",
    label: "Divider",
    description: "Horizontal line rule separator.",
    icon: Minus,
  },
  {
    type: "Spacer",
    label: "Spacer",
    description: "Vertical empty spacing gap.",
    icon: MoveVertical,
  },
];

const PRESET_ICONS: Record<string, typeof Box> = {
  Sparkles,
  ShoppingBag,
  ShieldCheck,
  Star,
  Mail,
};

export function AddTab() {
  const [activeCategory, setActiveCategory] = useState<"components" | "ecommerce">("components");
  const addNode = useEditorStore((s) => s.addNode);
  const insertSectionTemplate = useEditorStore((s) => s.insertSectionTemplate);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);

  const handleDragStart = (e: React.DragEvent, type: ComponentType) => {
    e.dataTransfer.setData("application/zirro-component", type);
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="zirro-tab-content zirro-add-tab">
      <div className="zirro-tab-header">
        <h3>Add to Page</h3>
      </div>

      {/* Segment Switcher */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          background: "var(--zirro-inspector-border, #1e293b)",
          padding: 3,
          borderRadius: 8,
          marginBottom: 14,
          gap: 4,
        }}
      >
        <button
          type="button"
          onClick={() => setActiveCategory("components")}
          style={{
            background: activeCategory === "components" ? "var(--ed-accent)" : "transparent",
            color: activeCategory === "components" ? "#ffffff" : "var(--ed-text-sec)",
            border: "none",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          Components
        </button>
        <button
          type="button"
          onClick={() => setActiveCategory("ecommerce")}
          style={{
            background: activeCategory === "ecommerce" ? "var(--ed-accent)" : "transparent",
            color: activeCategory === "ecommerce" ? "#ffffff" : "var(--ed-text-sec)",
            border: "none",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <ShoppingBag size={13} />
          E-Commerce
        </button>
      </div>

      {activeCategory === "components" ? (
        <>
          <p className="zirro-hint" style={{ marginBottom: 12 }}>
            {selectedNodeId
              ? "Click or drag component to insert."
              : "Click or drag component onto canvas."}
          </p>

          <div className="zirro-component-grid">
            {PALETTE_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.type}
                  type="button"
                  className="zirro-component-card"
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.type)}
                  onClick={() => addNode(selectedNodeId, item.type)}
                >
                  <span className="zirro-card-icon">
                    <Icon size={18} />
                  </span>
                  <div className="zirro-card-text">
                    <span className="zirro-card-label">{item.label}</span>
                    <span className="zirro-card-desc">{item.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div>
          <p className="zirro-hint" style={{ marginBottom: 12 }}>
            One-click insert pre-built, fully customizable e-commerce sections:
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ECOMMERCE_SECTION_PRESETS.map((preset) => {
              const Icon = PRESET_ICONS[preset.iconName] ?? ShoppingBag;
              return (
                <div
                  key={preset.id}
                  style={{
                    background: "rgba(30, 41, 59, 0.6)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    transition: "border-color 0.15s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: "var(--ed-accent-soft)",
                          color: "var(--ed-accent-text)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={16} />
                      </span>
                      <strong style={{ fontSize: 13, color: "var(--ed-text)" }}>{preset.name}</strong>
                    </div>
                    {preset.badge ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: 9999,
                          background: "rgba(245, 158, 11, 0.2)",
                          color: "#fbbf24",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {preset.badge}
                      </span>
                    ) : null}
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: "var(--ed-text-sec)", lineHeight: 1.4 }}>
                    {preset.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => insertSectionTemplate(preset.createSection())}
                    style={{
                      background: "var(--ed-accent-soft)",
                      border: "1px solid rgba(44, 75, 255, 0.4)",
                      color: "var(--ed-accent-text)",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--ed-accent)";
                      e.currentTarget.style.color = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "var(--ed-accent-soft)";
                      e.currentTarget.style.color = "var(--ed-accent-text)";
                    }}
                  >
                    <Plus size={14} />
                    Insert Section
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
